import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSEO } from '../lib/useSEO';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { sprintAPI } from '../lib/api';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Clock, ArrowLeft } from 'lucide-react';
import { AchievementPopup } from '../components/AchievementPopup';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function SprintPlay() {
  const { sessionId } = useParams();
  useSEO('Sprint Challenge | JepPrep', 'Race against the clock in a fast-paced trivia sprint. Quick answers earn bonus points.');
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [timeLeft, setTimeLeft] = useState(20);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [showingResult, setShowingResult] = useState(false);
  const [progress, setProgress] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [timedOut, setTimedOut] = useState(false);
  const inputRef = useRef(null);
  const timerRef = useRef(null);
  const isSubmittingRef = useRef(false);
  const showingResultRef = useRef(false);
  const questionStartTimeRef = useRef(null);  // Track when question was shown
  const currentQuestionRef = useRef(null);  // Ref to avoid stale closure
  const [achievementQueue, setAchievementQueue] = useState([]);
  const [currentAchievement, setCurrentAchievement] = useState(null);

  // Keep refs in sync with state
  useEffect(() => {
    isSubmittingRef.current = isSubmitting;
  }, [isSubmitting]);

  useEffect(() => {
    showingResultRef.current = showingResult;
  }, [showingResult]);

  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);

  const startTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTimeLeft(20);
    setTimedOut(false);
    questionStartTimeRef.current = Date.now();  // Track when question started
    
    timerRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          // Set timeout flag and trigger submission
          setTimedOut(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const submitAnswer = useCallback(async (ans, isTimeout = false) => {
    if (isSubmittingRef.current || !currentQuestionRef.current) return;
    setIsSubmitting(true);
    isSubmittingRef.current = true;
    clearInterval(timerRef.current);

    // Calculate time taken
    const timeTakenMs = questionStartTimeRef.current 
      ? Date.now() - questionStartTimeRef.current 
      : 20000;

    try {
      const response = await sprintAPI.answer(sessionId, {
        answer: ans,
        question_index: currentQuestionRef.current.index,
        time_taken_ms: isTimeout ? 20000 : timeTakenMs  // Full time if timed out
      });

      // Mark result with timeout info
      const resultData = {
        ...response.data,
        was_timeout: isTimeout
      };

      setLastResult(resultData);
      setShowingResult(true);
      showingResultRef.current = true;
      setProgress((p) => p + 1);

      // Queue achievement popups if any
      if (response.data.newly_unlocked_achievements && response.data.newly_unlocked_achievements.length > 0) {
        setAchievementQueue(prev => [...prev, ...response.data.newly_unlocked_achievements]);
      }

      // Show result for 2 seconds (shorter for timeouts)
      const displayTime = isTimeout ? 1500 : 2000;
      setTimeout(() => {
        setShowingResult(false);
        showingResultRef.current = false;
        setAnswer('');
        setLastResult(null);
        setTimedOut(false);

        if (response.data.complete) {
          navigate(`/sprint/${sessionId}/results`);
        } else if (response.data.next_question) {
          setCurrentQuestion(response.data.next_question);
          currentQuestionRef.current = response.data.next_question;
          setTimeLeft(20);
          startTimer();
          inputRef.current?.focus();
        }
        setIsSubmitting(false);
        isSubmittingRef.current = false;
      }, displayTime);
    } catch (error) {
      console.error('Sprint answer error:', error);
      setIsSubmitting(false);
      isSubmittingRef.current = false;
      setTimedOut(false);
      
      // For timeout errors, show a specific message but don't block
      if (isTimeout) {
        toast.error('Time ran out - moving to next question');
        // Force move to next by incrementing progress
        setProgress((p) => p + 1);
      } else {
        toast.error('Connection issue - please try again');
      }
      
      // Reset timer and allow retry
      setTimeLeft(20);
      startTimer();
      inputRef.current?.focus();
    }
  }, [sessionId, navigate, startTimer]);

  useEffect(() => {
    // Check if we have session data from navigation state
    if (location.state?.session) {
      setSession(location.state.session);
      setCurrentQuestion(location.state.currentQuestion);
      currentQuestionRef.current = location.state.currentQuestion;
      setTotalQuestions(location.state.totalQuestions);
      setTimeLeft(20);
      startTimer();
    } else {
      // No state passed - redirect back to sprint setup
      navigate('/sprint');
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionId, location.state, navigate, startTimer]);

  // Handle timeout via effect
  useEffect(() => {
    if (timedOut && !isSubmittingRef.current && !showingResultRef.current) {
      submitAnswer('', true);
    }
  }, [timedOut, submitAnswer]);

  const handleSubmit = (e) => {
    e.preventDefault();
    submitAnswer(answer);
  };

  const timerPercentage = (timeLeft / 20) * 100;
  const timerColor = timeLeft > 5 ? '' : timeLeft > 2 ? 'warning' : 'danger';

  // Process achievement queue - show one at a time
  useEffect(() => {
    if (!currentAchievement && achievementQueue.length > 0) {
      setCurrentAchievement(achievementQueue[0]);
      setAchievementQueue(prev => prev.slice(1));
    }
  }, [currentAchievement, achievementQueue]);

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
      {currentAchievement && (
        <AchievementPopup
          achievement={currentAchievement}
          onClose={() => setCurrentAchievement(null)}
        />
      )}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 items-center">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" size="sm" className="text-gray-300" onClick={() => navigate('/sprint')}>
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Sprint</span>
              </Button>
              <img src={LOGO_URL} alt="JepPrep" className="h-10 shrink-0" />
            </div>
          </div>
        </div>
      </nav>

      <div className="flex-1 p-4 pt-6 flex justify-center">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-gray-400 mb-2">
            <span>Question {progress + 1} of {totalQuestions}</span>
            <span className={`font-mono ${timeLeft <= 5 ? 'text-rose-400' : timeLeft <= 10 ? 'text-yellow-400' : 'text-white'}`}>
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />{timeLeft}s
            </span>
          </div>
          <div className="timer-bar">
            <div 
              className={`timer-bar-fill ${timerColor}`}
              style={{ width: `${timerPercentage}%` }}
            />
          </div>
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          {showingResult ? (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="text-center"
            >
              <Card className={`border-2 ${lastResult?.is_correct ? 'bg-green-500/10 border-green-500' : lastResult?.was_timeout ? 'bg-orange-500/10 border-orange-500' : 'bg-red-500/10 border-red-500'}`}>
                <CardContent className="p-8">
                  <div className="mb-4">
                    {lastResult?.is_correct ? (
                      <CheckCircle className="w-16 h-16 mx-auto text-green-500" />
                    ) : lastResult?.was_timeout ? (
                      <Clock className="w-16 h-16 mx-auto text-orange-500" />
                    ) : (
                      <XCircle className="w-16 h-16 mx-auto text-red-500" />
                    )}
                  </div>
                  <h2 className={`text-2xl font-display mb-4 ${lastResult?.is_correct ? 'text-green-500' : lastResult?.was_timeout ? 'text-orange-500' : 'text-red-500'}`}>
                    {lastResult?.is_correct ? 'Correct!' : lastResult?.was_timeout ? "Time's Up!" : 'Incorrect'}
                  </h2>
                  <p className="text-gray-300">Correct answer:</p>
                  <p className="text-white text-xl font-semibold">{lastResult?.correct_answer}</p>
                </CardContent>
              </Card>
            </motion.div>
          ) : currentQuestion ? (
            <motion.div
              key={currentQuestion.index}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <Card className="clue-display mb-6">
                <div className="text-[hsl(var(--brand-gold-500))] text-sm font-semibold mb-4">
                  {currentQuestion.category}
                </div>
                <div className="text-white text-xl md:text-2xl" data-testid="sprint-question">
                  {currentQuestion.question}
                </div>
              </Card>

              <form onSubmit={handleSubmit}>
                <div className="flex gap-3">
                  <Input
                    ref={inputRef}
                    type="text"
                    placeholder="Type your answer..."
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    autoFocus
                    disabled={isSubmitting}
                    className="flex-1 bg-[hsl(var(--accent))] border-[hsl(var(--border))] text-white text-lg py-6"
                    data-testid="sprint-answer-input"
                  />
                  <Button
                    type="submit"
                    disabled={isSubmitting || !answer.trim()}
                    className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-8 py-6"
                    data-testid="sprint-submit-button"
                  >
                    Submit
                  </Button>
                </div>
              </form>
            </motion.div>
          ) : (
            <div className="text-center text-gray-400">Loading...</div>
          )}
        </AnimatePresence>
      </div>
      </div>
    </div>
  );
}
